import { Route, Routes, useSearchParams } from "react-router-dom";

import { BackfillDetails, ExportStatusDashboard, ImportStatusDashboard } from "../components";
import { KeyDetails } from "../features/keys/pages/key-details";
import MonacoEditorPage from "../features/monacoEditor/pages/monacoEditor";
import { DownloadBrowser } from "../pages/downloads/DownloadsPage";
import { EventLogBrowser } from "../pages/events/EventsPage";
import { IntegrationBrowser } from "../pages/integrations";
import { DraftBrowser } from "../pages/scripts/Drafts";
import { HistoryBrowser } from "../pages/scripts/History";
import { PublishedBrowser } from "../pages/scripts/Published/Published";
import { TestRuns } from "../pages/testRuns";
import { UploadBrowser } from "../pages/uploads";

import * as Sentry from "@sentry/react";
import { AuthenticationGuard } from "../components/AuthenticationGuard";
import { ToDashboard } from "../components/RouteHelpers";
import Permissions from "../constants/permissions";
import { AccountDetailsPage, UserDetailsPage } from "../features/adminUI/pages";
import { AdminPage } from "../features/adminUI/pages/AdminPage";
import { Homepage } from "../features/data-under-management/pages/Homepage";
import { HomepageDetailsPage } from "../features/data-under-management/pages/HomepageDetailsPage";
import { EmailIngest } from "../features/emailIngest";
import { ErrorPage } from "../features/errorPage";
import { DownloadsChartsPage } from "../features/homepage/DownloadsChartsPage";
import KeysPage from "../features/keys/pages";
import { AddKey } from "../features/keys/pages/add-key";
import { EditKey } from "../features/keys/pages/edit-key";
import { BackfillsCommonPage, BackfillsPage } from "../pages/backfills";
import { SchemaReport, SourceReport } from "../pages/reports";
import FlowsPage from "../features/flows-visualization/pages/FlowsPage";
import { PrivateAccountRoute, PrivateGlobalRoute } from "./RouteComponents";

const RedirectToTestRuns = () => {
  const [searchParams] = useSearchParams();

  if (searchParams.get("type=testRun")) {
    return <TestRuns />;
  } else {
    return <ToDashboard />;
  }
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const Router = () => {
  return (
    <SentryRoutes>
      <Route
        path="/users/:id"
        element={
          <AuthenticationGuard
            component={() => (
              <PrivateGlobalRoute
                permission={Permissions.ManageUsers}
                title="User Details"
                element={<UserDetailsPage />}
              />
            )}
          />
        }
      />
      <Route
        path="/admin"
        element={
          <AuthenticationGuard
            component={() => (
              <PrivateGlobalRoute permission={Permissions.ListAccountsV2} title="Accounts" element={<AdminPage />} />
            )}
          />
        }
      />

      <Route
        path="/app/:url_name/dashboard"
        element={<AuthenticationGuard component={() => <PrivateAccountRoute title="Home" element={<Homepage />} />} />}
      />
      <Route
        path="/app/:url_name/homepage-details"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Home" element={<HomepageDetailsPage />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/homepage/charts"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Charts" element={<DownloadsChartsPage />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/homepage"
        element={<AuthenticationGuard component={() => <PrivateAccountRoute title="Home" element={<Homepage />} />} />}
      />

      <Route
        path="/app/:url_name/backfills"
        element={
          <AuthenticationGuard
            component={() => (
              <PrivateAccountRoute
                permission={Permissions.Backfill}
                title="Backfills"
                element={<BackfillsCommonPage />}
              />
            )}
          />
        }
      >
        <Route path="list" element={<BackfillsPage />}></Route>
        <Route path="details/:backfillId" element={<BackfillDetails />} />
      </Route>
      <Route
        path="/app/:url_name/email-ingest"
        element={
          <AuthenticationGuard
            component={() => (
              <PrivateAccountRoute
                title="Email Ingest"
                permission={Permissions.EmailIngestV3}
                element={<EmailIngest />}
              />
            )}
          />
        }
      />
      <Route
        path="/app/:url_name/downloads/list"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Download Files" element={<DownloadBrowser />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/imports/dashboard"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Import Status" element={<ImportStatusDashboard />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/uploads/list"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Upload Files" element={<UploadBrowser />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/exports/dashboard"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Export Status" element={<ExportStatusDashboard />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/events"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Events Log" element={<EventLogBrowser />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/keys"
        element={<AuthenticationGuard component={() => <PrivateAccountRoute title="Keys" element={<KeysPage />} />} />}
      />
      <Route
        path="/app/:url_name/keys/details"
        element={
          <AuthenticationGuard component={() => <PrivateAccountRoute title="Key Detail" element={<KeyDetails />} />} />
        }
      />

      <Route
        path="/app/:url_name/keys/add-key"
        element={<AuthenticationGuard component={() => <PrivateAccountRoute title="Add key" element={<AddKey />} />} />}
      />

      <Route
        path="/app/:url_name/keys/edit-key"
        element={
          <AuthenticationGuard component={() => <PrivateAccountRoute title="Edit key" element={<EditKey />} />} />
        }
      />

      <Route
        path="/app/:url_name/integrations"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Integrations" element={<IntegrationBrowser />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/scripts/published"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Published" element={<PublishedBrowser />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/scripts/drafts"
        element={
          <AuthenticationGuard component={() => <PrivateAccountRoute title="Drafts" element={<DraftBrowser />} />} />
        }
      />
      <Route
        path="/app/:url_name/scripts/editor/:version_id/"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Switchboard Script Editor" element={<MonacoEditorPage />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/scripts/tests/*"
        element={<AuthenticationGuard component={() => <PrivateAccountRoute title="Tests" element={<TestRuns />} />} />}
      />
      <Route
        path="/app/:url_name/scripts/history"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Published History" element={<HistoryBrowser />} />}
          />
        }
      />
      <Route
        path="/app/:url_name/reports/source"
        element={
          <AuthenticationGuard
            component={() => (
              <PrivateAccountRoute
                title="Source Reports"
                permission={Permissions.ListAccounts}
                element={<SourceReport />}
              />
            )}
          />
        }
      />
      <Route
        path="/app/:url_name/reports/schema"
        element={
          <AuthenticationGuard
            component={() => (
              <PrivateAccountRoute
                title="Schema Reports"
                permission={Permissions.ListAccounts}
                element={<SchemaReport />}
              />
            )}
          />
        }
      />
      <Route
        path="/app/:url_name/editor/scripts"
        element={
          <AuthenticationGuard
            component={() => <PrivateAccountRoute title="Tests" element={<RedirectToTestRuns />} />}
          />
        }
      />
      <Route
        path="/:url_name/users"
        element={
          <AuthenticationGuard
            component={() => (
              <PrivateGlobalRoute
                permission={Permissions.ManageUsers}
                title="Account Details"
                element={<AccountDetailsPage />}
              />
            )}
          />
        }
      />

      <Route
        path="/app/:url_name/flows"
        element={
          <AuthenticationGuard
            component={() => (
              <PrivateAccountRoute title="Flows" permission={Permissions.HiddenIntegrations} element={<FlowsPage />} />
            )}
          />
        }
      />

      <Route path="/error" element={<ErrorPage />} />
      <Route path="*" element={<ToDashboard />} />
    </SentryRoutes>
  );
};
